import React, { useState, useEffect } from "react";
import "./stripe-account-connected.scss";
import {
    GSForm,
    GSSidePanelPage,
    GSLoadingSpinnerOverlay,
    defaultLayouts,
    defaultGapping,
    defaultTypography,
    golfstatusColors,
    START,
    CENTER
} from "golfstatus_react_components";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import stripeLogo from "../../images/Stripe.svg";

import {
    selectCurrentOrganization,
    clearNotifications,
    selectOrganizationNotifications,
    connectStripeAccount,
    selectOrganization,
} from "../../reducers/organizationSlice";
import { getSuccessTitle, successForm } from "../../forms/SuccessForm";
import { errorForm, getErrorTitle } from "../../forms/ErrorForm";
import { useNotificationBanner } from "../../hooks/notificationHooks";

const StripeAccountConnected = () => {
    const orgNotifications = useSelector(selectOrganizationNotifications);
    const selectedOrganization = useSelector(selectCurrentOrganization);

    const [searchParams] = useSearchParams();
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const stripeToken = searchParams.get("code");
    const orgId = searchParams.get("state");

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if(stripeToken && orgId) {
            console.log("dispatching org update");
            setLoading(true);
            dispatch(connectStripeAccount({ id: orgId, stripeSetupToken: stripeToken })).finally(() => setLoading(false));
        } else if (orgId) {
            setLoading(true);
            dispatch(selectOrganization(orgId)).finally(() => setLoading(false));
        }
    }, [orgId, stripeToken, dispatch]);

    const getActions = () => {
        if (loading) {
            return [];
        } if (error) {
            return [
                { name: "Try Again", action: connectStripe, type: "orange" },
                { name: "Close", action: nextNavigation, type: "grey" }
            ];
        } else {
            return [{ name: "done", action: nextNavigation, type: "grey" }];
        }
    };

    const connectStripe = () => {
        let clientId = process.env.REACT_APP_STRIPE_CLIENT_ID;
        let uri = responseUri();

        window.open(`https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${uri}&state=${orgId}`);
    }

    const responseUri = () => `${process.env.REACT_APP_GOLFSTATUS_ORGANIZATIONS_URL}/stripe-account-connected`;

    const nextNavigation = () => {
        dispatch(clearNotifications());
        navigate(`/${orgId}/settings/payments`);
    };

    const getNavigation = () => {
        let title = "Stripe Account Connecting";
        if (selectedOrganization?.stripeId) {
            title = "Stripe Account Connected";
        } else if (error) {
            title = "Stripe Connection Error";
        }

        return {
            title,
            leftIcon: faChevronLeft,
            leftButtonClick: nextNavigation,
        };
    };

    const confirmationForm = (
        <div style={{...defaultLayouts.vStack, ...defaultGapping.xLargeGap}}>
            <div style={{...defaultLayouts.hStack.align(START, CENTER), ...defaultGapping.mediumGap}}>
                <img src={stripeLogo} alt="stripe logo" />
                <div style={{...defaultTypography.body}}>{selectedOrganization?.stripeId}</div>
            </div>
            <div style={{height: "1px", ...defaultLayouts.fullWidth, background: golfstatusColors.grey100, ...defaultLayouts.hStack}}></div>
            <div style={{...defaultLayouts.vStack}}>
                <div style={{...defaultTypography.bodyRegular.withOpacity()}}>Stripe Confirmation Code</div>
                <div style={{...defaultTypography.body}}>{stripeToken}</div>
            </div>
        </div>
    );

    const getContent = () => {
        if (loading) {
            return <GSLoadingSpinnerOverlay />
        }

        if (error) {
            return (
                <GSForm
                    formTitle={getErrorTitle("Stripe Connection Error")}
                    formSections={errorForm(
                        `Something went wrong linking this account to ${selectedOrganization?.name}. Try connecting again, or contact support if the problem continues.`,
                        errorDescription,
                    )}
                />
            );
        } else if (stripeToken && orgId && selectedOrganization?.stripeId) {
            return (
                <GSForm
                    formTitle={getSuccessTitle("Stripe Account Connected")}
                    formSections={successForm(
                        `This Stripe account is connected to ${selectedOrganization?.name || "N/A"}`,
                        confirmationForm,
                    )}
                ></GSForm>
            );
        }
    };

    const getDrawer = () => {
        return {
            actions: getActions(),
        };
    };

    const timeoutAction = () => {
        dispatch(clearNotifications());
    };

    let notificationSettings = {
        notifications: [...orgNotifications],
        timeoutAction,
    };
  
    const [bannerNotifications] = useNotificationBanner(notificationSettings);

    return (
        <stripe-account-connected>
        <GSSidePanelPage
            header={getNavigation()}
            banner={bannerNotifications}
            content={getContent()}
            drawer={getDrawer()}
        />
        </stripe-account-connected>
    );
};

export default StripeAccountConnected;
